import { Theme, alpha } from "@mui/material";
import { PriorityHigh, Person, SmartToy, SvgIconComponent } from "@mui/icons-material";
import { TipoEmissorMensagem } from "../../enum/atendimento/TipoEmissorMensagem.enum";

/**
 * Identidade visual do atendimento — a mesma da landing (`website`,
 * `ConversasMock`), trazida para o sistema.
 *
 * A landing é só clara e escreve cor literal; aqui a tela tem tema claro e
 * escuro, então as superfícies são derivadas do tema em vez de fixadas. O que
 * se mantém idêntico é o vocabulário: os raios, a escala tipográfica miúda, as
 * cores semânticas de estado e a ideia de tudo nascer de um mesmo tom base com
 * alfas diferentes — no claro um azul (nunca cinza neutro, que suja os cards),
 * no escuro o branco.
 */

/** Raios de canto. Mesmos valores da landing. */
export const raio = {
    sm: "10px",
    md: "14px",
    lg: "20px",
    bolha: "13px",
    pill: "999px",
} as const;

export const cores = {
    /** Azul da marca. */
    marca: {
        700: "#084A9C",
        600: "#0B5FC0",
        500: "#137FEC",
        400: "#3D9BFF",
        300: "#8AC4FF",
    },
    /** Secundária da marca. */
    sinal: {
        600: "#B22F51",
        500: "#DA4167",
        400: "#E8748D",
    },
    /**
     * Amarelo do produto: é o amarelo literal com que a IA marca a conversa.
     * Não usar como cor decorativa — só para sinalizar "precisa de gente".
     */
    alerta: {
        700: "#7A4E00",
        600: "#9A6200",
        500: "#FFB020",
        400: "#FFC65C",
    },
    /** Verde WhatsApp — só quando o contexto é literalmente o WhatsApp/IA no ar. */
    whats: {
        600: "#128C4B",
        500: "#25D366",
    },
} as const;

/** Estados de atendimento de uma conversa. */
export type EstadoAtendimento = "ia" | "atencao" | "respondido" | "operador";

interface MetaEstado {
    /**
     * Sem rótulo, a lista não desenha etiqueta nenhuma — o estado só pinta a
     * conversa. Etiqueta é só para o que pede ação de gente: a IA tocando é o
     * caso normal, e alerta já respondido segue amarelo, mas calado.
     */
    rotulo?: string;
    cor: string;
    icone: SvgIconComponent;
}

export const ESTADO_ATENDIMENTO: Record<EstadoAtendimento, MetaEstado> = {
    ia: { cor: cores.whats[500], icone: SmartToy },
    atencao: { rotulo: "Precisa de você", cor: cores.alerta[500], icone: PriorityHigh },
    respondido: { cor: cores.alerta[500], icone: PriorityHigh },
    operador: { rotulo: "Em atendimento", cor: cores.marca[400], icone: Person },
};

/** Emissores do lado da loja: falou alguém nosso, não o cliente. */
const EMISSORES_DA_LOJA: TipoEmissorMensagem[] = [
    TipoEmissorMensagem.ATENDENTE,
    TipoEmissorMensagem.ATENDENTE_IA,
];

/**
 * A conversa está esperando resposta nossa?
 *
 * O alerta da IA não se apaga sozinho — quem remove é o operador, depois de
 * resolver. Só que entre responder o cliente e remover o alerta a conversa
 * seguia gritando "precisa de você", mesmo já respondida. A pergunta que
 * importa é outra: quem falou por último? Se foi a loja, o cliente não está
 * esperando; o alerta continua aberto (e a conversa, amarela), mas não é essa
 * que precisa de você agora.
 *
 * Na dúvida — sem última mensagem, ou emissor que não é do nosso lado — vale
 * "precisa responder": é melhor cobrar uma resposta a mais do que engolir uma
 * conversa parada.
 */
export const precisaResponder = (emissorUltimaMensagem?: TipoEmissorMensagem): boolean =>
    !emissorUltimaMensagem || !EMISSORES_DA_LOJA.includes(emissorUltimaMensagem);

/**
 * Estado de uma conversa a partir do que a tela já sabe: alerta da IA vence
 * atribuição, porque é o que precisa de ação.
 */
export const estadoDaConversa = (
    temAlerta: boolean,
    temOperador: boolean,
    aguardandoResposta = true,
): EstadoAtendimento => {
    if (temAlerta) return aguardandoResposta ? "atencao" : "respondido";
    if (temOperador) return "operador";
    return "ia";
};

export type ModoTema = "light" | "dark";

/** Tom base dos overlays: azul no claro, branco no escuro. */
const tomBase = (modo: ModoTema) => (modo === "dark" ? "#FFFFFF" : "#174691");

/**
 * Superfície derivada do tom base — equivalente ao `rgba(var(--sf-ov), α)` da
 * landing. Use dentro de `sx` (recebe o tema do MUI).
 */
export const superficie = (theme: Theme, alfa: number) =>
    alpha(tomBase(theme.palette.mode as ModoTema), alfa);

/** Mesma superfície para quem só tem o `themeName` do ThemeContext do app. */
export const superficieModo = (modo: ModoTema, alfa: number) => alpha(tomBase(modo), alfa);

/** Borda padrão dos blocos. */
export const contorno = (theme: Theme, alfa = 0.1) => `1px solid ${superficie(theme, alfa)}`;

/** Texto sobre o amarelo do alerta, legível nos dois temas. */
export const textoAlerta = (theme: Theme) =>
    theme.palette.mode === "dark" ? cores.alerta[400] : cores.alerta[600];

export const textoAlertaForte = (theme: Theme) =>
    theme.palette.mode === "dark" ? cores.alerta[400] : cores.alerta[700];

/** Gradiente do CTA primário. */
export const gradienteMarca = `linear-gradient(100deg, ${cores.marca[500]} 0%, ${cores.marca[600]} 100%)`;

/**
 * Ação principal (concluir pedido, adicionar ao carrinho, vincular...).
 *
 * O desabilitado precisa ser declarado: o `Mui-disabled` do MUI troca apenas o
 * `background-color`, e o gradiente — que é `background-image` — continuava
 * pintado por baixo, deixando o rótulo em azul sobre azul. Aqui o gradiente é
 * removido de fato e o rótulo vai para a cor de texto desabilitado.
 */
export const acaoPrincipal = (theme: Theme) => ({
    textTransform: "none" as const,
    fontWeight: 800,
    boxShadow: "none",
    color: "#fff",
    background: gradienteMarca,
    "&:hover": { background: gradienteMarca, boxShadow: "none" },
    "&.Mui-disabled": {
        background: "none",
        backgroundColor: superficie(theme, 0.12),
        color: theme.palette.text.disabled,
    },
});

/**
 * Escala tipográfica da landing. Miúda de propósito: é uma tela densa, e o que
 * dá respiro é o espaçamento, não o tamanho da fonte.
 */
export const texto = {
    /** Rótulo de seção em caixa alta. */
    secao: {
        fontSize: "11px",
        fontWeight: 800,
        letterSpacing: "0.08em",
        textTransform: "uppercase" as const,
    },
    /** Nome do contato, título de produto. */
    titulo: { fontSize: "13px", fontWeight: 700 },
    /** Corpo: prévia de mensagem, descrição. */
    corpo: { fontSize: "12px" },
    /** Apoio: horário, trilha, contador. */
    apoio: { fontSize: "10.5px" },
    /** Etiqueta de estado. */
    etiqueta: { fontSize: "10px", fontWeight: 700, letterSpacing: "0.03em" },
} as const;

/**
 * Limita o texto a N linhas sem cortar palavra no meio.
 *
 * É o que permite nome de produto e de opção longos conviverem numa coluna
 * estreita: em vez de truncar em X caracteres, o texto quebra e só reticencia
 * se passar do limite de linhas.
 */
export const limitarLinhas = (linhas: number) => ({
    display: "-webkit-box",
    WebkitBoxOrient: "vertical" as const,
    WebkitLineClamp: linhas,
    overflow: "hidden",
    overflowWrap: "anywhere" as const,
});

/** Anel pulsante do estado de atenção. Anima transform/opacity, não box-shadow. */
export const pulsoAtencao = {
    "&::after": {
        content: '""',
        position: "absolute",
        inset: 0,
        borderRadius: "50%",
        border: `1px solid ${cores.alerta[500]}`,
        animation: "pulsoAtencao 1.9s ease-out infinite",
    },
    "@keyframes pulsoAtencao": {
        "0%": { transform: "scale(1)", opacity: 0.7 },
        "70%": { transform: "scale(2.4)", opacity: 0 },
        "100%": { transform: "scale(2.4)", opacity: 0 },
    },
    "@media (prefers-reduced-motion: reduce)": {
        "&::after": { animation: "none", opacity: 0 },
    },
} as const;

/** Entrada das bolhas de mensagem. */
export const entradaMensagem = (indice: number) => ({
    animation: "entradaMensagem 0.4s cubic-bezier(0.16,1,0.3,1) both",
    animationDelay: `${Math.min(indice, 8) * 0.05}s`,
    "@keyframes entradaMensagem": {
        from: { opacity: 0, transform: "translateY(8px)" },
        to: { opacity: 1, transform: "translateY(0)" },
    },
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
});
